import { create } from "zustand"
import type { ShotModalKey, ShotLeftPanelKey } from "@/components/shot-modal-sidebar"
import type { ShotPanelBootstrap } from "@/lib/workspace-api"

type LegacyShotModalKey = ShotModalKey | "prompts"

export type PreviewTabKey = "video" | "storyboard" | "shooting_board" | "animatic" | "previz"

export type ComposerTabKey = "storyboard" | "video" | "shooting_board" | "assistant"

/** Right column of the shot panel. Independent of the entity left panel. */
export type ShotViewMode = "classic" | "flow"

export type PrevizDepthVariant = "gray" | "vis"

const LEFT_PANELS: ShotLeftPanelKey[] = [
  "prompt",
  "assistant",
  "characters",
  "location",
  "props",
  "storyboard_versions",
  "shooting_board_versions",
  "video_versions",
  "post_production_versions",
  "flow",
]

const VALID_PREVIEW_TABS: PreviewTabKey[] = ["video", "storyboard", "shooting_board", "animatic", "previz"]

function parsePreviewTab(raw: string | null): PreviewTabKey {
  if (raw && (VALID_PREVIEW_TABS as string[]).includes(raw)) {
    return raw as PreviewTabKey
  }
  return "video"
}

/** Align Кадр/Видео/Раскадровка with the right preview. Assistant stays put. Animatic and previz leave the composer. */
export function composerTabForPreview(previewTab: PreviewTabKey, current: ComposerTabKey): ComposerTabKey {
  if (current === "assistant") return "assistant"
  if (previewTab === "video" || previewTab === "storyboard" || previewTab === "shooting_board") return previewTab
  return current
}

// Map a (possibly legacy) sidebar/view key to the left-panel surface it now lives in.
// The right column is always the preview, so "preview"/"prompts" return to the prompt.
function viewToLeftPanel(view: LegacyShotModalKey | undefined): ShotLeftPanelKey {
  switch (view) {
    case "characters":
    case "location":
    case "props":
    case "storyboard_versions":
    case "shooting_board_versions":
    case "video_versions":
    case "post_production_versions":
      return view
    default:
      return "prompt"
  }
}

function isShotPanelHome(leftPanel: ShotLeftPanelKey): boolean {
  return leftPanel === "prompt" || leftPanel === "assistant" || leftPanel === "flow"
}

function applyShotViewParams(params: URLSearchParams, leftPanel: ShotLeftPanelKey, shotViewMode: ShotViewMode) {
  if (shotViewMode === "flow" && isShotPanelHome(leftPanel)) {
    params.set("panel", "flow")
    return
  }
  if (!isShotPanelHome(leftPanel)) {
    params.set("panel", leftPanel)
  }
  if (shotViewMode === "flow") {
    params.set("mode", "flow")
  }
}

export function shotPanelHash(
  shotId: number,
  previewTab: PreviewTabKey,
  leftPanel: ShotLeftPanelKey,
  shotViewMode: ShotViewMode,
  opts?: { scaleSheetMixed?: boolean },
): string {
  const params = new URLSearchParams()
  params.set("shot", String(shotId))
  params.set("tab", previewTab)
  applyShotViewParams(params, leftPanel, shotViewMode)
  if (opts?.scaleSheetMixed) {
    params.set("scale_sheet", "mixed")
  }
  return `#${params.toString()}`
}

function setHash(
  projectId: number,
  shotId: number,
  previewTab: PreviewTabKey,
  leftPanel: ShotLeftPanelKey,
  shotViewMode: ShotViewMode,
  opts?: { scaleSheetMixed?: boolean },
) {
  void projectId
  const hash = shotPanelHash(shotId, previewTab, leftPanel, shotViewMode, opts)
  if (window.location.hash !== hash) {
    // Сохраняем текущий history.state: Inertia хранит там (зашифрованную) страницу,
    // и затирание его null ломает обработку popstate/bfcache.
    window.history.replaceState(window.history.state, "", hash)
  }
}

function clearHash() {
  if (window.location.hash) {
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search)
  }
}

/** Drop scale_sheet=mixed from the current shot hash without closing the panel. */
export function clearMixedScaleSheetHash() {
  const hash = window.location.hash
  if (!hash.startsWith("#")) return
  const params = new URLSearchParams(hash.slice(1))
  if (params.get("scale_sheet") !== "mixed") return
  params.delete("scale_sheet")
  const next = `#${params.toString()}`
  if (window.location.hash !== next) {
    window.history.replaceState(window.history.state, "", next)
  }
}

export function isWorkspaceEntityHash(hash: string): boolean {
  return (
    /^#character-\d+(\/outfit-\d+)?$/.test(hash) ||
    /^#location-\d+(\/variant-\d+)?$/.test(hash) ||
    /^#prop-\d+(\/state-\d+)?$/.test(hash)
  )
}

const SHOT_PANEL_PATH_RE = /\/workspace\/(storyboard|video|animatics|assembly|studio|library)$/

export function isShotPanelPagePath(pathname: string): boolean {
  return SHOT_PANEL_PATH_RE.test(pathname)
}

export function parseShotHash(hash: string): {
  shotId: number
  previewTab: PreviewTabKey
  leftPanel: ShotLeftPanelKey
  shotViewMode: ShotViewMode
  openMixedScaleSheet: boolean
} | null {
  if (!hash.startsWith("#")) return null
  const params = new URLSearchParams(hash.slice(1))
  const rawId = params.get("shot")
  if (!rawId) return null
  const shotId = Number(rawId)
  if (!Number.isFinite(shotId) || shotId <= 0) return null
  let leftPanel: ShotLeftPanelKey = "prompt"
  let shotViewMode: ShotViewMode = "classic"
  // Backward compat: an old characters/location/*_versions "view" now lives in the left panel.
  const rawView = params.get("view") as LegacyShotModalKey | null
  if (rawView) {
    leftPanel = viewToLeftPanel(rawView)
  }
  const rawPanel = params.get("panel")
  if (rawPanel === "flow") {
    shotViewMode = "flow"
    leftPanel = "prompt"
  } else if (rawPanel && (LEFT_PANELS as string[]).includes(rawPanel)) {
    leftPanel = rawPanel as ShotLeftPanelKey
  }
  if (params.get("mode") === "flow") {
    shotViewMode = "flow"
  }
  const previewTab = parsePreviewTab(params.get("tab"))
  const openMixedScaleSheet = params.get("scale_sheet") === "mixed"
  return { shotId, previewTab, leftPanel, shotViewMode, openMixedScaleSheet }
}

type ShotPanelState = {
  open: boolean
  projectId: number | null
  shotId: number | null
  leftPanel: ShotLeftPanelKey
  shotViewMode: ShotViewMode
  previewTab: PreviewTabKey
  composerTab: ComposerTabKey
  pendingDefaultTab: boolean
  explicitPreviewTab: boolean
  pendingOpenMixedScaleSheet: boolean
  /** Bumped by the toolbar «Назад к …» button; ShotPanel flushes prompts then closes. */
  closeRequestId: number
  /** While the shot's 3D editor is open, toolbar back closes the editor instead of the shot. */
  scene3dReturnClose: (() => void) | null
  /** While the shot's 3D room is open, toolbar back closes the room instead of the shot. */
  roomViewerReturnClose: (() => void) | null
  workspaceBootstrap: ShotPanelBootstrap | null
  /** Серая или цветная маска глубины превиза. Общая для карточки отправки и вкладки «Превиз». */
  previzDepthVariant: PrevizDepthVariant
  setPrevizDepthVariant: (variant: PrevizDepthVariant) => void
  openShotPanel: (p: {
    projectId: number
    shotId: number
    view?: LegacyShotModalKey
    leftPanel?: ShotLeftPanelKey
    shotViewMode?: ShotViewMode
    previewTab?: PreviewTabKey
    preserveTab?: boolean
    openMixedScaleSheet?: boolean
  }) => void
  consumePendingOpenMixedScaleSheet: () => boolean
  setLeftPanel: (p: ShotLeftPanelKey) => void
  /** Selects a view mode and returns the left column to that mode's home. */
  setShotViewMode: (mode: ShotViewMode) => void
  setPreviewTab: (t: PreviewTabKey) => void
  setComposerTab: (t: ComposerTabKey) => void
  clearPendingDefaultTab: () => void
  setWorkspaceBootstrap: (bootstrap: ShotPanelBootstrap | null) => void
  requestClose: () => void
  setScene3dReturnClose: (close: (() => void) | null) => void
  setRoomViewerReturnClose: (close: (() => void) | null) => void
  close: (opts?: { preserveLocationHash?: boolean }) => void
}

export const useShotPanelStore = create<ShotPanelState>((set, get) => ({
  open: false,
  projectId: null,
  shotId: null,
  leftPanel: "prompt",
  shotViewMode: "classic",
  previewTab: "video",
  composerTab: "storyboard",
  pendingDefaultTab: false,
  explicitPreviewTab: false,
  pendingOpenMixedScaleSheet: false,
  closeRequestId: 0,
  scene3dReturnClose: null,
  roomViewerReturnClose: null,
  workspaceBootstrap: null,
  previzDepthVariant: "gray",
  setPrevizDepthVariant: (previzDepthVariant) => set({ previzDepthVariant }),
  openShotPanel: ({
    projectId,
    shotId,
    view,
    leftPanel,
    shotViewMode,
    previewTab,
    preserveTab,
    openMixedScaleSheet,
  }) => {
    const prev = get()
    const rawLeft: ShotLeftPanelKey = leftPanel ?? viewToLeftPanel(view)
    const isAssistant = rawLeft === "assistant"
    const isFlowPanel = rawLeft === "flow"
    const safeLeftPanel: ShotLeftPanelKey = isAssistant || isFlowPanel ? "prompt" : rawLeft
    const nextMode: ShotViewMode = shotViewMode ?? (isFlowPanel ? "flow" : preserveTab ? prev.shotViewMode : "classic")
    const tab = preserveTab ? prev.previewTab : (previewTab ?? (prev.shotId === shotId ? prev.previewTab : "video"))
    const composerTab: ComposerTabKey = isAssistant
      ? "assistant"
      : prev.shotId === shotId
        ? prev.composerTab
        : composerTabForPreview(tab, prev.composerTab)
    const clearBootstrap = prev.projectId !== projectId
    const wantMixed = openMixedScaleSheet === true
    const pendingMixed =
      wantMixed || (prev.shotId === shotId && prev.pendingOpenMixedScaleSheet && openMixedScaleSheet !== false)
    set({
      open: true,
      projectId,
      shotId,
      leftPanel: safeLeftPanel,
      shotViewMode: nextMode,
      previewTab: tab,
      composerTab,
      pendingDefaultTab: !preserveTab,
      explicitPreviewTab: !preserveTab && previewTab != null,
      pendingOpenMixedScaleSheet: pendingMixed,
      ...(clearBootstrap ? { workspaceBootstrap: null } : {}),
      ...(prev.shotId !== shotId ? { previzDepthVariant: "gray" as const } : {}),
    })
    setHash(projectId, shotId, tab, safeLeftPanel, nextMode, { scaleSheetMixed: pendingMixed })
  },
  consumePendingOpenMixedScaleSheet: () => {
    const pending = get().pendingOpenMixedScaleSheet
    if (pending) {
      set({ pendingOpenMixedScaleSheet: false })
    }
    clearMixedScaleSheetHash()
    return pending
  },
  setLeftPanel: (leftPanel) => {
    if (leftPanel === "flow") {
      set({ shotViewMode: "flow", leftPanel: "prompt" })
    } else if (leftPanel === "assistant") {
      set({ leftPanel: "prompt", composerTab: "assistant", shotViewMode: "classic" })
    } else {
      set({ leftPanel })
    }
    const { open, projectId, shotId, previewTab, leftPanel: panel, shotViewMode, pendingOpenMixedScaleSheet } = get()
    // Не пишем hash после close(): shotId ещё живёт в сторе, иначе URL снова #shot=…
    if (open && projectId && shotId) {
      setHash(projectId, shotId, previewTab, panel, shotViewMode, { scaleSheetMixed: pendingOpenMixedScaleSheet })
    }
  },
  setShotViewMode: (shotViewMode) => {
    set({ shotViewMode, leftPanel: "prompt" })
    const { open, projectId, shotId, previewTab, pendingOpenMixedScaleSheet } = get()
    if (open && projectId && shotId) {
      setHash(projectId, shotId, previewTab, "prompt", shotViewMode, { scaleSheetMixed: pendingOpenMixedScaleSheet })
    }
  },
  setComposerTab: (composerTab) => {
    set({ composerTab })
  },
  setPreviewTab: (previewTab) => {
    const composerTab = composerTabForPreview(previewTab, get().composerTab)
    set({ previewTab, composerTab, pendingDefaultTab: false, explicitPreviewTab: false })
    const { open, projectId, shotId, leftPanel, shotViewMode, pendingOpenMixedScaleSheet } = get()
    if (open && projectId && shotId) {
      setHash(projectId, shotId, previewTab, leftPanel, shotViewMode, { scaleSheetMixed: pendingOpenMixedScaleSheet })
    }
  },
  clearPendingDefaultTab: () => set({ pendingDefaultTab: false, explicitPreviewTab: false }),
  setWorkspaceBootstrap: (workspaceBootstrap) => set({ workspaceBootstrap }),
  requestClose: () => set((state) => ({ closeRequestId: state.closeRequestId + 1 })),
  setScene3dReturnClose: (scene3dReturnClose) => set({ scene3dReturnClose }),
  setRoomViewerReturnClose: (roomViewerReturnClose) => set({ roomViewerReturnClose }),
  close: (opts) => {
    set({
      open: false,
      pendingDefaultTab: false,
      explicitPreviewTab: false,
      pendingOpenMixedScaleSheet: false,
      workspaceBootstrap: null,
      scene3dReturnClose: null,
      roomViewerReturnClose: null,
    })
    if (!opts?.preserveLocationHash) {
      clearHash()
    }
  },
}))
